import { ActionIcon, Button, FileButton, Loader, SegmentedControl, Stack, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  Blend,
  Captions,
  FolderOpen,
  Image as ImageIcon,
  Sparkles,
  Type,
  Upload,
  Video,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";

import { useDeleteAsset, useListRecentFolders } from "../../api/generated/endpoints/assets/assets";
import type { EffectKind, TransitionKind } from "../../api/generated/model";
import { MediaMime } from "../../api/generated/zod";
import { refreshLibraryLists } from "../../api/library-lists";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useLibraryPages } from "../../platform/media/library-pages";
import {
  ANY_MEDIA,
  UploadState,
  useMediaUpload,
  type MediaUpload,
} from "../../platform/media/media-upload";
import { useRefuseStrayFileDrops } from "../../platform/media/stray-drops";
import { usePrefetchTrashDays, useTrashStay } from "../../platform/media/trash-days";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { ConfirmModal } from "../../ui/ConfirmModal";
import {
  actorItem,
  assetItem,
  mergeNewest,
  outputItems,
  readTo,
  useBrandJobs,
  useTeamActors,
  type BinItem,
} from "./bin-sources";
import {
  BinList,
  SOURCES_OF,
  TypeFilterButton,
  type AddMedia,
  type BinFilter,
  type MorePages,
} from "./BinList";
import { faceName, loadFaces } from "./fonts";
import { TEXT_DRAG_TYPE, TEXT_PRESETS, presetFace, type TextPreset } from "./text-presets";
import type { AspectRatio } from "./project";
import type { MediaKind, Timeline, TimelineEdit } from "./timeline";
import { CaptionsTab } from "./captions/CaptionsTab";
import type { Said } from "./captions/Transcript";
import type { GenerateCaptions } from "./captions/use-generate-captions";
import { EffectsTab } from "./EffectsTab";
import type { Playhead } from "./playhead";
import { TransitionsTab } from "./TransitionsTab";
import classes from "./editor.module.css";

/** Live's media panel tabs, in its order. */
const TABS = [
  { id: "uploads", label: "Uploads", Icon: FolderOpen },
  { id: "images", label: "Images", Icon: ImageIcon },
  { id: "videos", label: "Videos", Icon: Video },
  { id: "text", label: "Text", Icon: Type },
  { id: "captions", label: "Captions", Icon: Captions },
  { id: "transitions", label: "Transitions", Icon: Blend },
  { id: "effects", label: "Effects", Icon: Sparkles },
] as const;

type BinTab = (typeof TABS)[number]["id"];

export interface MediaBinProps {
  teamId: string;
  brandId: string;
  /** The kinds the project holds the most of: none of them is added. */
  full: ReadonlySet<MediaKind>;
  /** The member may not change this project: nothing is added. */
  readOnly: boolean;
  onAdd: AddMedia;
  /** False at the most titles a project holds, or when the member may not change it. */
  canAddTitle: boolean;
  /** Adds live's "Add Text", or one of its presets. */
  onAddTitle: (preset: TextPreset | null) => void;
  /** Live's transition tile: its kind on the cut nearest the playhead (#240). */
  onApplyTransition: (kind: TransitionKind) => void;
  /** A tile's drag to the timeline starts (its kind) or ends (null). */
  onDragTransition: (kind: TransitionKind | null) => void;
  /** Counts live's "+" on a cut: each one opens the Transitions tab. */
  transitionsOpened: number;
  /** False at the most effects a project holds, or when the member may not change it. */
  canAddEffect: boolean;
  /** Live's effect tile: the effect from 0 to 5 s on a new top track (#265). */
  onAddEffect: (kind: EffectKind) => void;
  /** Live's Captions tab (#239): the timeline it reads and edits, and the playhead. */
  timeline: Timeline;
  aspectRatio: AspectRatio;
  /** Read by the captions' transcript alone, so a seek renders neither this panel nor its tabs. */
  playhead: Playhead;
  onEdit: (edit: TimelineEdit) => void;
  onSeek: (ms: number) => void;
  /** The chosen caption, if one is chosen: the Captions tab opens, as live's. */
  chosenCaption: string | null;
  /**
   * The editor's one caption run (#239), which the assistant's Auto Captions starts too (#242),
   * and its line, kept while another tab is open.
   */
  generation: GenerateCaptions;
  captionsSaid: Said | null;
  onCaptionsSay: (said: Said | null) => void;
  /**
   * On a phone (spec phone-layout.md), the sheet this panel shows, its rail hidden: "media" is
   * Uploads, Images and Videos in one segmented control. Null while its sheet is shut; absent on
   * the desktop.
   */
  phoneTab?: BinSheet | null | undefined;
}

/** The media panel's sheets on a phone: its own tabs, with Uploads, Images and Videos as one. */
export type BinSheet = "media" | "text" | "captions" | "transitions" | "effects";
const MEDIA_TABS: readonly BinTab[] = ["uploads", "images", "videos"];

/**
 * The media panel (live's left column): a rail of tabs, icon over label, and the chosen tab.
 * "Transitions" is live's (#240).
 * "Uploads" are the brand's library files (videos, pictures and sounds; live's manual uploads),
 * with live's upload button and drop; "Images" and "Videos" every picture and video of the brand
 * (its library's and its jobs' delivered outputs), newest first, with live's filter. A click puts
 * a file at the end of the first track of its kind. "Text" adds a title.
 */
export function MediaBin(props: MediaBinProps) {
  const [tab, setTab] = useState<BinTab>("uploads");
  // Live's "+" on a cut opens the Transitions tab.
  const [opened, setOpened] = useState(props.transitionsOpened);
  if (opened !== props.transitionsOpened) {
    setOpened(props.transitionsOpened);
    setTab("transitions");
  }
  // A caption chosen on the canvas or the timeline opens the Captions tab (#239), as live's.
  const [caption, setCaption] = useState(props.chosenCaption);
  if (caption !== props.chosenCaption) {
    setCaption(props.chosenCaption);
    if (props.chosenCaption !== null) setTab("captions");
  }
  // A phone's sheet turns to its tab; Media keeps whichever of its three was open.
  const [sheet, setSheet] = useState(props.phoneTab ?? null);
  if ((props.phoneTab ?? null) !== sheet) {
    setSheet(props.phoneTab ?? null);
    if (props.phoneTab === "media") {
      if (!MEDIA_TABS.includes(tab)) setTab("uploads");
    } else if (props.phoneTab !== undefined && props.phoneTab !== null) setTab(props.phoneTab);
  }
  const panelId = useId();
  // The panel's scroller: Images and Videos read their next page as their end scrolls into it.
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  // A file dropped outside Uploads would open in the tab and close the project.
  useRefuseStrayFileDrops();
  // Here, not in the Uploads tab, so an upload carries on while another tab is open; it ends
  // with the editor.
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useMediaUpload({
    teamId: props.teamId,
    brandId: props.brandId,
    kinds: ANY_MEDIA,
    // Live lists the new file first and adds nothing to the timeline.
    onUploaded: () => undefined,
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  return (
    <aside className={classes.bin} aria-label="Media bin" data-phone={props.phoneTab !== undefined || undefined}>
      <div className={classes.rail} role="tablist" aria-label="Media source" aria-orientation="vertical">
        {TABS.map((each) => {
          const icon = <each.Icon size={15} aria-hidden />;
          const active = tab === each.id;
          return (
            <button
              key={each.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={panelId}
              data-active={active}
              className={classes.railTab}
              onClick={() => setTab(each.id)}
            >
              {icon}
              {each.label}
            </button>
          );
        })}
      </div>
      <div ref={setBody} className={classes.binBody} role="tabpanel" id={panelId} aria-label={TITLE[tab]}>
        {props.phoneTab === "media" ? (
          <SegmentedControl
            className={classes.binSources}
            aria-label="Media source"
            fullWidth
            value={tab}
            onChange={(value) => setTab(value as BinTab)}
            data={MEDIA_TABS.map((id) => ({ value: id, label: TITLE[id] }))}
          />
        ) : null}
        {props.full.size > 0 && (tab === "uploads" || tab === "images" || tab === "videos") ? (
          <Text size="xs" c="var(--cai-color-text-muted)">
            {props.full.has("audio") && !props.full.has("video")
              ? "This project has the most sounds it can hold (20). Remove one to add another."
              : "This project has the most clips it can hold (50). Remove one to add another."}
          </Text>
        ) : null}
        {tab === "uploads" ? <UploadsTab {...props} upload={upload} /> : null}
        {tab === "images" ? <BrandMedia {...props} kind="image" scroller={body} /> : null}
        {tab === "videos" ? <BrandMedia {...props} kind="video" scroller={body} /> : null}
        {tab === "text" ? <TextTab canAdd={props.canAddTitle} onAdd={props.onAddTitle} /> : null}
        {tab === "transitions" ? (
          <TransitionsTab
            canApply={!props.readOnly}
            onApply={props.onApplyTransition}
            onDrag={props.onDragTransition}
          />
        ) : null}
        {tab === "effects" ? (
          <EffectsTab canApply={props.canAddEffect} onApply={props.onAddEffect} />
        ) : null}
        {tab === "captions" ? (
          <CaptionsTab
            teamId={props.teamId}
            timeline={props.timeline}
            aspectRatio={props.aspectRatio}
            playhead={props.playhead}
            readOnly={props.readOnly}
            onEdit={props.onEdit}
            onSeek={props.onSeek}
            chosenCaption={props.chosenCaption}
            generation={props.generation}
            said={props.captionsSaid}
            onSay={props.onCaptionsSay}
          />
        ) : null}
      </div>
    </aside>
  );
}

const TITLE: Record<BinTab, string> = {
  uploads: "Uploads",
  images: "Images",
  videos: "Videos",
  text: "Text",
  captions: "Captions",
  transitions: "Transitions",
  effects: "Effects",
};

/**
 * Live's text panel: "Add Text" puts live's new text at the playhead, and each of live's ten
 * presets its own, its tile showing its word in its face (live's tiles are images of the same).
 * Each drags onto the timeline too (issue 241), carrying its preset's name ("" for Add Text).
 */
function TextTab({ canAdd, onAdd }: { canAdd: boolean; onAdd: (preset: TextPreset | null) => void }) {
  // The tiles' faces, loaded with the panel; a tile shows the fallback face until its own is in.
  useEffect(() => {
    void loadFaces(TEXT_PRESETS.map(presetFace));
  }, []);
  const drag = (name: string) => (event: DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(TEXT_DRAG_TYPE, name);
  };
  return (
    <Stack gap="md">
      <Button
        className={classes.addText}
        disabled={!canAdd}
        draggable={canAdd}
        onDragStart={drag("")}
        onClick={() => onAdd(null)}
      >
        Add Text
      </Button>
      <ul className={classes.presetGrid} aria-label="Text styles">
        {TEXT_PRESETS.map((preset) => (
          <li key={preset.name}>
            <button
              type="button"
              className={classes.preset}
              aria-label={`${preset.name} style`}
              disabled={!canAdd}
              draggable={canAdd}
              onDragStart={drag(preset.name)}
              onClick={() => onAdd(preset)}
            >
              <span
                style={{
                  fontFamily: `"${faceName(preset.font)}", sans-serif`,
                  fontWeight: preset.weight,
                  fontSize: preset.tile.size,
                }}
                data-chip={preset.tile.chip === true || undefined}
              >
                {preset.label}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Stack>
  );
}

/** What the chooser takes: the library's types (live's `image/*,video/*,audio/*`). */
const ACCEPT = MediaMime.options.join(",");

/** Live's pages: Uploads 48 at a time, Images and Videos 24 (`uploads.tsx`, `assets.ts`). */
const UPLOADS_PAGE = 48;
const BRAND_PAGE = 24;
/** Live's search waits this long after the last key before it asks (`uploads.tsx`, `images.tsx`). */
export const SEARCH_WAIT_MS = 500;

/**
 * Live's Uploads: the brand's library files, newest first, 48 a page ("Load More"), searched on
 * the server 500 ms after the last key ("No matches found"; the search shows once there are files
 * or a search, as live's). The upload button beside the search takes several files at once, and
 * so does a drop on the panel;
 * they go up one after another through the library's one upload (S1) and stop at the first that
 * fails, saying why. With no files yet the panel is live's empty state: a full-width "Upload" and
 * the drop area. Live's bin on a tile deletes the file; here it asks first and moves it to the
 * library's trash, as the library does, on the files the member may delete (D6).
 */
function UploadsTab(props: MediaBinProps & { upload: MediaUpload }) {
  const { teamId, brandId, full, readOnly, onAdd, upload } = props;
  const [query, setQuery] = useState("");
  const [search] = useDebouncedValue(query.trim(), SEARCH_WAIT_MS);
  const pages = useLibraryPages(teamId, brandId, { trash: false, search }, true, {
    size: UPLOADS_PAGE,
    keepPrevious: true,
    watchCopies: true,
  });
  const [dragging, setDragging] = useState(false);
  // Clears the chooser after each pick, so the same file can be chosen again.
  const resetChooser = useRef<() => void>(null);
  const queryClient = useQueryClient();
  const me = useMembership(teamId);
  const [deleting, setDeleting] = useState<BinItem | undefined>(undefined);
  const stay = useTrashStay(teamId, brandId, deleting !== undefined);
  const prefetchDays = usePrefetchTrashDays(teamId, brandId);
  const remove = useDeleteAsset({
    mutation: {
      onSuccess: async () => {
        setDeleting(undefined);
        await refreshLibraryLists(queryClient, teamId, brandId);
      },
      // The hint that offered the bin may be stale: the scope and the list are read again.
      onError: () =>
        Promise.all([
          refreshAccessScope(queryClient),
          refreshLibraryLists(queryClient, teamId, brandId),
        ]),
    },
  });
  const sendAll = async (files: readonly File[]) => {
    for (const file of files) {
      if (!(await upload.send(file))) return;
    }
  };
  const carriesFiles = (event: DragEvent) => event.dataTransfer.types.includes("Files");
  const drop = {
    onDragOver: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragging(true);
    },
    onDragLeave: (event: DragEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node && event.currentTarget.contains(next))) setDragging(false);
    },
    onDrop: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragging(false);
      if (!upload.pending) void sendAll(Array.from(event.dataTransfer.files));
    },
  };
  const chooser = (button: (props: { onClick: () => void }) => ReactNode) => (
    <FileButton
      multiple
      accept={ACCEPT}
      disabled={upload.pending}
      resetRef={resetChooser}
      onChange={(files) => {
        void sendAll(files);
        resetChooser.current?.();
      }}
      inputProps={{ "aria-label": "Choose files to upload" }}
    >
      {button}
    </FileButton>
  );
  if (pages.isPending) return <Loader size="sm" aria-label="Loading the library's files" />;
  if (pages.isError) return <ErrorAlert error={pages.error} />;
  const items = pages.data.pages
    .flatMap((page) => page.items)
    .filter((asset) => asset.status === "ready")
    .map((asset) => assetItem(teamId, brandId, asset));
  // Live's empty state only with no files at all: a search that finds none keeps its box.
  const none = items.length === 0 && !pages.hasNextPage && query === "" && search === "";
  return (
    <div className={classes.uploads} {...drop}>
      {none ? (
        <>
          {chooser((props) => (
            <Button
              {...props}
              variant="default"
              className={classes.uploadWide}
              leftSection={<Upload size={16} aria-hidden />}
              disabled={upload.pending}
            >
              Upload
            </Button>
          ))}
          <UploadState upload={upload} />
          <DropArea />
        </>
      ) : (
        <>
          <UploadState upload={upload} />
          <BinList
            items={items}
            empty=""
            nothingFound="No matches found"
            searchLabel="Search uploads..."
            query={query}
            onQuery={setQuery}
            tool={chooser((props) => (
              <ActionIcon
                {...props}
                variant="default"
                size={36}
                className={classes.searchTool}
                aria-label="Upload"
                disabled={upload.pending}
              >
                <Upload size={16} aria-hidden />
              </ActionIcon>
            ))}
            more={
              pages.hasNextPage
                ? { loading: pages.isFetchingNextPage, load: () => void pages.fetchNextPage() }
                : undefined
            }
            remove={{
              may: (item) =>
                me !== undefined && canEditContent(me.role, item.createdBy ?? "", me.userId),
              ask: setDeleting,
              near: prefetchDays,
            }}
            full={full}
            readOnly={readOnly}
            onAdd={onAdd}
          />
        </>
      )}
      <ConfirmModal
        opened={deleting !== undefined}
        title={`Delete ${deleting?.media.name ?? "this file"}?`}
        confirmLabel="Move to trash"
        onConfirm={() => {
          const assetId = deleting?.source.kind === "asset" ? deleting.source.assetId : null;
          if (assetId !== null) remove.mutate({ teamId, brandId, assetId });
        }}
        onCancel={() => {
          remove.reset();
          setDeleting(undefined);
        }}
        pending={remove.isPending}
        error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
      >
        <Text size="sm">
          It stays {stay}, where anyone who may delete it can restore it. A clip
          made from it stays on the timeline but cannot be rendered until it is restored.
        </Text>
      </ConfirmModal>
      {dragging && !none ? (
        <div className={classes.dropCover}>
          <DropArea />
        </div>
      ) : null}
    </div>
  );
}

/** Live's drop area: the upload icon over "Drag and drop videos, photos, and audio files here". */
function DropArea() {
  return (
    <div className={classes.dropArea}>
      <Upload size={40} aria-hidden />
      <Text size="xs" c="var(--cai-color-text-muted)">
        Drag and drop videos, photos, and audio files here
      </Text>
    </div>
  );
}

const BRAND_MEDIA = {
  image: { search: "Search brand images...", empty: "No brand images found" },
  video: { search: "Search brand videos...", empty: "No brand videos found" },
} as const;

/**
 * Live's Images and Videos: every picture (or video) of the brand, newest first: its library's
 * files and its jobs' delivered outputs, an output saved into the library shown once. Both lists
 * are read page by page and merged (the list's end, scrolled into view, reads the one that is
 * behind, as live's, spec capture-fixes). The library is
 * searched and filtered on the server, 24 a page (`search`, its type's `source`, the current
 * folder's `folder_id`); the outputs, which the jobs' list cannot search, are matched by name here
 * and shown only where a generation may be (All Types, Creative, no folder). With or without a
 * search, nothing found says live's "No brand images found" / "No brand videos found".
 */
function BrandMedia(props: MediaBinProps & { kind: "image" | "video"; scroller: HTMLElement | null }) {
  const { teamId, brandId, kind, scroller, full, readOnly, onAdd } = props;
  const [query, setQuery] = useState("");
  const [search] = useDebouncedValue(query.trim(), SEARCH_WAIT_MS);
  const [filter, setFilter] = useState<BinFilter>({ type: "all", project: "all" });
  // Current Project: the folder the member used last in this brand (A3c), as live's active one.
  const recent = useListRecentFolders(teamId, brandId);
  const folderId = filter.project === "current" ? recent.data?.[0]?.id : undefined;
  // Live's Avatar: the team's actors, pictures no folder holds; a video tab has none.
  const avatars = filter.type === "avatar";
  const actorsListed = avatars && kind === "image" && filter.project === "all";
  const actors = useTeamActors(teamId, brandId, search, actorsListed);
  const library = useLibraryPages(
    teamId,
    brandId,
    {
      kind,
      trash: false,
      search,
      source: SOURCES_OF[filter.type === "avatar" ? "all" : filter.type],
      folderId,
    },
    // A current folder waits for the read that names it.
    !avatars && (filter.project === "all" || !recent.isPending),
    { size: BRAND_PAGE, keepPrevious: true, watchCopies: kind === "video" },
  );
  const jobs = useBrandJobs(teamId, brandId);
  // A generation's output is no folder's, and only a generation's type lists it.
  const withOutputs =
    folderId === undefined && (filter.type === "all" || filter.type === "creative");
  const list = (items: readonly BinItem[], more: MorePages | undefined) => (
    <BinList
      items={items}
      empty={BRAND_MEDIA[kind].empty}
      nothingFound={BRAND_MEDIA[kind].empty}
      searchLabel={BRAND_MEDIA[kind].search}
      query={query}
      onQuery={setQuery}
      tool={<TypeFilterButton value={filter} onChange={setFilter} />}
      wide
      more={more}
      scroller={scroller}
      full={full}
      readOnly={readOnly}
      onAdd={onAdd}
    />
  );
  if (avatars) {
    if (!actorsListed) return list([], undefined);
    if (actors.isPending) return <Loader size="sm" aria-label="Loading the team's actors" />;
    if (actors.isError) return <ErrorAlert error={actors.error} />;
    const items = actors.data.pages.flatMap((page) => page.items).map((each) => actorItem(teamId, each));
    return list(
      items,
      actors.hasNextPage
        ? { loading: actors.isFetchingNextPage, load: () => void actors.fetchNextPage() }
        : undefined,
    );
  }
  if (library.isPending || jobs.isPending) {
    return <Loader size="sm" aria-label={`Loading the brand's ${kind}s`} />;
  }
  if (library.isError) return <ErrorAlert error={library.error} />;
  if (jobs.isError) return <ErrorAlert error={jobs.error} />;
  const assets = library.data.pages.flatMap((page) => page.items);
  const runs = jobs.data.pages.flatMap((page) => page.items);
  const streams = [
    {
      items: assets
        .filter((asset) => asset.status === "ready")
        .map((asset) => assetItem(teamId, brandId, asset)),
      readTo: readTo(assets),
      more: library.hasNextPage,
    },
    {
      items: withOutputs
        ? runs
            .flatMap((job) => outputItems(teamId, job, kind))
            .filter((item) => item.media.name.toLowerCase().includes(search.toLowerCase()))
        : [],
      readTo: readTo(runs),
      more: withOutputs && jobs.hasNextPage,
    },
  ];
  const merged = mergeNewest(streams);
  const next = merged.next === 0 ? library : merged.next === 1 ? jobs : null;
  return list(
    merged.items,
    next === null
      ? undefined
      : { loading: next.isFetchingNextPage, load: () => void next.fetchNextPage() },
  );
}
